import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import {
  GET_TICKETING_LAUNCHED,
  GET_TICKETING_SETTING,
} from 'api/ticketing/queries';
import { SET_TICKETING_SETTING } from 'api/ticketing/mutations';
// Types
import { GetTicketingSetting } from 'api/ticketing/types/GetTicketingSetting';
import { GetTicketingLaunched } from 'api/ticketing/types/GetTicketingLaunched';
import {
  SetTicketingSetting,
  SetTicketingSettingVariables,
} from 'api/ticketing/types/SetTicketingSetting';
import { TicketingSettingFields } from 'api/ticketing/types/TicketingSettingFields';
import { SetTicketingSettingInput } from 'api/graphql-global-types';

export type SaveTicketingSetting = {
  /** Resolves to the setting as saved, or null when the server sent none back. */
  saveSetting: (
    input: SetTicketingSettingInput
  ) => Promise<TicketingSettingFields | null>;
  busy: boolean;
};

export const useSaveTicketingSetting = (): SaveTicketingSetting => {
  const [setTicketingSetting, { loading }] = useMutation<
    SetTicketingSetting,
    SetTicketingSettingVariables
  >(SET_TICKETING_SETTING, {
    // The setting has no id, so the cached read is updated by hand instead of refetched
    update: (cache, { data: result }) => {
      if (result) {
        cache.writeQuery<GetTicketingSetting>({
          query: GET_TICKETING_SETTING,
          data: { getTicketingSetting: result.setTicketingSetting },
        });
        // Shows or hides the ticketing menus right away
        cache.writeQuery<GetTicketingLaunched>({
          query: GET_TICKETING_LAUNCHED,
          data: { ticketingLaunched: result.setTicketingSetting.isLaunched },
        });
      }
    },
  });

  const saveSetting = useCallback(
    async (
      input: SetTicketingSettingInput
    ): Promise<TicketingSettingFields | null> => {
      const { data } = await setTicketingSetting({ variables: { input } });

      return data?.setTicketingSetting || null;
    },
    [setTicketingSetting]
  );

  return { saveSetting, busy: loading };
};
